import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18n from '../../i18n/i18n';
import {
  IReEnrollmentStudentListResponse,
  IReEnrollmentStudentState,
  IStudentJourneyResponse,
  IStudentJourneyState,
} from '../../interface/reEnrollment-student.interface';
import {
  fetchReEnrollmentStudents,
  fetchStudentJourney,
  enrollInitialStudents,
  crossCurriculumTransfer,
  unifiedTransition,
} from '../action/reEnrollment-student.action';

const initialState: IReEnrollmentStudentState = {
  students: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
  },
  isLoading: false,
  isTransitioning: false,
  error: null,
  enrollmentSummary: null,
  crossCurriculumSummary: null,
  unifiedTransitionSummary: null,
  journeyState: {
    studentId: null,
    journey: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10,
    },
    isLoading: false,
    error: null,
  },
};

const journeyInitialState: IStudentJourneyState = {
  studentId: null,
  journey: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },
  isLoading: false,
  error: null,
};

const reEnrollmentStudentSlice = createSlice({
  name: 'reEnrollmentStudent',
  initialState,
  reducers: {
    resetStudents: (state) => {
      state.students = [];
      state.pagination = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 20,
      };
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetJourney: (state) => {
      state.journeyState = journeyInitialState;
    },
    clearJourneyError: (state) => {
      if (!state.journeyState) {
        state.journeyState = journeyInitialState;
      }
      state.journeyState.error = null;
    },
    clearEnrollmentSummary: (state) => {
      state.enrollmentSummary = null;
    },
    clearCrossCurriculumSummary: (state) => {
      state.crossCurriculumSummary = null;
    },
    clearUnifiedTransitionSummary: (state) => {
      state.unifiedTransitionSummary = null;
    },
  },
  extraReducers: (builder) => {
    // Enroll students initially
    builder
      .addCase(enrollInitialStudents.pending, (state) => {
        state.isTransitioning = true;
        state.error = null;
        state.enrollmentSummary = null;
      })
      .addCase(enrollInitialStudents.fulfilled, (state, action) => {
        state.isTransitioning = false;
        state.error = null;
        state.enrollmentSummary = action.payload.data;
      })
      .addCase(enrollInitialStudents.rejected, (state, action) => {
        state.isTransitioning = false;
        state.error = action.payload?.message || i18n.t('toast.failed_to_enroll_students', 'Failed to enroll students.');
        state.enrollmentSummary = null;
      });
    builder
      // Fetch re-enrollment students
      .addCase(fetchReEnrollmentStudents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.students = [];
        state.pagination = initialState.pagination;
      })
      .addCase(
        fetchReEnrollmentStudents.fulfilled,
        (state, action: PayloadAction<IReEnrollmentStudentListResponse>) => {
          state.isLoading = false;
          state.students = action.payload.data.students;
          state.pagination = {
            currentPage: action.payload.data.currentPage,
            totalPages: action.payload.data.totalPages,
            totalItems: action.payload.data.totalItems,
            itemsPerPage: action.payload.data.itemsPerPage,
          };
        },
      )
      .addCase(fetchReEnrollmentStudents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || i18n.t('toast.failed_to_fetch_re_enrollment_students', 'Failed to fetch re-enrollment students.');
        state.students = [];
        state.pagination = initialState.pagination;
      })
      // Fetch student journey
      .addCase(fetchStudentJourney.pending, (state) => {
        if (!state.journeyState) {
          state.journeyState = journeyInitialState;
        }
        state.journeyState.isLoading = true;
        state.journeyState.error = null;
        state.journeyState.journey = [];
        state.journeyState.pagination = journeyInitialState.pagination;
      })
      .addCase(
        fetchStudentJourney.fulfilled,
        (state, action: PayloadAction<IStudentJourneyResponse>) => {
          if (!state.journeyState) {
            state.journeyState = journeyInitialState;
          }
          state.journeyState.isLoading = false;
          state.journeyState.studentId = action.payload.data.studentId;
          state.journeyState.journey = action.payload.data.journey;
          state.journeyState.pagination = {
            currentPage: action.payload.data.currentPage,
            totalPages: action.payload.data.totalPages,
            totalItems: action.payload.data.totalItems,
            itemsPerPage: action.payload.data.itemsPerPage,
          };
        },
      )
      .addCase(fetchStudentJourney.rejected, (state, action) => {
        if (!state.journeyState) {
          state.journeyState = journeyInitialState;
        }
        state.journeyState.isLoading = false;
        state.journeyState.error =
          action.payload?.message || i18n.t('toast.failed_to_fetch_student_journey_logs', 'Failed to fetch student journey logs.');
        state.journeyState.journey = [];
        state.journeyState.pagination = journeyInitialState.pagination;
      });
    // Cross-Curriculum Transfer
    builder
      .addCase(crossCurriculumTransfer.pending, (state) => {
        state.isTransitioning = true;
        state.error = null;
        state.crossCurriculumSummary = null;
      })
      .addCase(crossCurriculumTransfer.fulfilled, (state, action) => {
        state.isTransitioning = false;
        state.error = null;
        state.crossCurriculumSummary = action.payload.data;
      })
      .addCase(crossCurriculumTransfer.rejected, (state, action) => {
        state.isTransitioning = false;
        state.error = action.payload?.message || i18n.t('toast.failed_to_transfer_students_across_curricula', 'Failed to transfer students across curricula.');
        state.crossCurriculumSummary = null;
      });
    
    builder
      .addCase(unifiedTransition.pending, (state) => {
        state.isTransitioning = true;
        state.error = null;
        state.unifiedTransitionSummary = null;
      })
      .addCase(unifiedTransition.fulfilled, (state, action) => {
        state.isTransitioning = false;
        state.error = null;
        state.unifiedTransitionSummary = action.payload.data;
      })
      .addCase(unifiedTransition.rejected, (state, action) => {
        state.isTransitioning = false;
        state.error = action.payload?.message || i18n.t('toast.failed_to_transition_students', 'Failed to transition students.');
        state.unifiedTransitionSummary = null;
      });
  },
});

export const {
  resetStudents,
  clearError,
  resetJourney,
  clearJourneyError,
  clearEnrollmentSummary,
  clearCrossCurriculumSummary,
  clearUnifiedTransitionSummary,
} = reEnrollmentStudentSlice.actions;

export default reEnrollmentStudentSlice.reducer;
